import { AllocationConstraintResponse } from '@/app/models/allocationConstraint';
import { useRemoveAllocationConstraintMutation } from '@/app/services/allocationConstraintApi';
import TruncatedTextWithTooltip from '@/app/utils/truncatedTextWithTooltip';
import ActionMenu, { ItemAction } from '@/components/common/ActionMenu';
import DataTable, { bodyCellSx, Column } from '@/components/common/DataTable';
import CancelOrConfirmDialog from '@/components/common/dialogs/CancelOrConfirmDialog';
import { TableCell, useTheme } from '@mui/material';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

type EventTableParams = {
  rows: AllocationConstraintResponse[];
  setSelectedItem: (p: AllocationConstraintResponse) => void;
  setDrawerOpen: (open: boolean) => void;
};

export default function AllocationConstraintTable(props: EventTableParams) {
  const theme = useTheme();
  const [selectedBadge, setSelectedBadge] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<boolean>(false);

  const { t } = useTranslation(['allocationConstraint', 'common']);
  const { rows, setSelectedItem, setDrawerOpen } = props;

  const [deleteAllocationConstraint] = useRemoveAllocationConstraintMutation();

  const handleDialogClose = () => {
    setDeleteDialog(false);
  };

  const handleDeleteItem = async () => {
    if (selectedBadge) {
      const response = await deleteAllocationConstraint(selectedBadge);
      if ('data' in response) {
        toast.dismiss();
        toast.success(
          t('collaborator.removed.success', {
            position: toast.POSITION.TOP_RIGHT,
          }),
        );
      } else {
        toast.error(
          t('common:processing.failed', {
            position: toast.POSITION.TOP_RIGHT,
          }),
        );
      }
      handleDialogClose();
      setSelectedBadge(null);
    }
  };

  const columns: Column[] = [
    { key: 'collaborator', label: t('common:collaborator'), width: theme.spacing(80) },
    { key: 'projects', label: t('common:projects') },
    { key: 'actions', label: t('common:actions'), align: 'right' },
  ];

  const renderRow = (row: AllocationConstraintResponse) => (
    <>
      <TableCell sx={bodyCellSx}>{`${row.person.badge} - ${row.person.name}`}</TableCell>
      <TableCell sx={{ ...bodyCellSx }}>
        <TruncatedTextWithTooltip sx={{ ...bodyCellSx }} value={row.codes.join(',')} size={110} />
      </TableCell>
      <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
        <ActionMenu>
          <ItemAction
            text={t('common:edit') as string}
            actionType="edit"
            onClick={() => {
              setSelectedItem(row);
              setDrawerOpen(true);
            }}
          />
          <ItemAction
            text={t('common:delete') as string}
            actionType="delete"
            onClick={() => {
              setSelectedBadge(row.person.badge);
              setDeleteDialog(true);
            }}
          />
        </ActionMenu>
      </TableCell>
    </>
  );

  // Use the row ID as the unique key
  const getRowKey = (row: AllocationConstraintResponse) => row.person.badge;

  const deleteDialogProps = {
    title: t('common:confirm.delete.title'),
    contentText: <Trans>{t('common:confirm.delete.message')}</Trans>,
  };

  return (
    <>
      <DataTable
        columns={columns}
        data={rows}
        renderRow={renderRow}
        getRowKey={getRowKey}
        emptyMessage={t('common:no.data.for.the.chosen.period.and.search.term')}
        stickyHeader
        size="small"
      />

      <CancelOrConfirmDialog
        title={deleteDialogProps.title}
        contentText={deleteDialogProps.contentText}
        open={deleteDialog}
        onClose={() => handleDialogClose()}
        onConfirm={async () => handleDeleteItem()}
      />
    </>
  );
}
